import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownOptions, InputFieldNumber, Modal } from '../UI';
import { Button } from '../UI/base/Button';
import { TreatmentPlanTemplateResponse, WeekDateRangeResponse } from '../../api/generated/api';
import {
  buildTreatmentPlanRolloutSummary,
  calculateTemplateSpan,
  calculateTreatmentPlanPlacements,
  type TreatmentPlanRolloutSelection
} from '../../services/treatmentPlanRolloutService';
import { formatDate } from '../../utils/dateUtils';
import {
  DEFAULT_WEEKS_BETWEEN_PLANS,
  MIN_PLAN_COUNT_FOR_GAP,
  ROLLOUT_SUMMARY_KEYS,
  WEEKS_BETWEEN_PLANS_OPTIONS
} from './addTreatmentPlanConstants';
import styles from './AddTreatmentPlanModal.module.css';

interface AddTreatmentPlanModalProps {
  treatmentPlanTemplateId: string;
  weekNumber: number;
  mode: 'single' | 'series';
  weeks: WeekDateRangeResponse[];
  treatmentPlanTemplates: TreatmentPlanTemplateResponse[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: TreatmentPlanRolloutSelection) => void;
}

export default function AddTreatmentPlanModal({
  treatmentPlanTemplateId,
  weekNumber,
  mode,
  weeks,
  treatmentPlanTemplates,
  isOpen,
  onClose,
  onConfirm
}: AddTreatmentPlanModalProps) {
  const { t, i18n } = useTranslation();

  const [capacity, setCapacity] = useState<number>(0);
  const [planCount, setPlanCount] = useState<number>(0);
  const [weeksBetweenPlans, setWeeksBetweenPlans] = useState<number>(DEFAULT_WEEKS_BETWEEN_PLANS);
  const [startWeek, setStartWeek] = useState<WeekDateRangeResponse | null>(null);

  const resetForm = () => {
    setCapacity(0);
    setPlanCount(0);
    setWeeksBetweenPlans(DEFAULT_WEEKS_BETWEEN_PLANS);
    setStartWeek(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const template = useMemo(
    () => treatmentPlanTemplates.find((tpt) => tpt.id === treatmentPlanTemplateId),
    [treatmentPlanTemplates, treatmentPlanTemplateId]
  );

  const templateName = template?.name ?? '';

  const planSpan = useMemo(
    () => calculateTemplateSpan(template?.weeks.map((w) => w.weekNumber) ?? []),
    [template]
  );

  const defaultStartWeek = useMemo(
    () => weeks.find((w) => w.weekNumber === weekNumber) ?? null,
    [weeks, weekNumber]
  );

  const activeStartWeek = startWeek ?? defaultStartWeek;
  const lastWeekNumber = weeks.length > 0 ? weeks[weeks.length - 1].weekNumber : 0;

  // Same calculation the roll-out itself runs, so the summary can never promise
  // something different from what gets added.
  const placements = useMemo(() => {
    if (!activeStartWeek || planCount < 1) return [];
    return calculateTreatmentPlanPlacements({
      startWeekNumber: activeStartWeek.weekNumber,
      planCount,
      weeksBetweenPlans,
      planSpan,
      lastWeekNumber
    });
  }, [activeStartWeek, planCount, weeksBetweenPlans, planSpan, lastWeekNumber]);

  const weekDisplay = (week: WeekDateRangeResponse) => {
    const label = t('add-treatment-plan-batch-week-display', { weekNumber: week.weekNumber });
    return (
      <>
        {label}{' '}
        <span className={styles.weekDates}>
          {formatDate(week.startDate, i18n.language)} - {formatDate(week.endDate, i18n.language)}
        </span>
      </>
    );
  };

  const modalTitle =
    mode === 'single'
      ? t('add-treatment-plan-batch-title-single')
      : t('add-treatment-plan-batch-title-series');

  // The series in plain language, worded by the service so it stays testable and
  // reads off the same placements the roll-out uses.
  const summary = useMemo(() => {
    const built = buildTreatmentPlanRolloutSummary(placements, weeksBetweenPlans, lastWeekNumber);
    return built && t(ROLLOUT_SUMMARY_KEYS[built.variant], built.values);
  }, [placements, weeksBetweenPlans, lastWeekNumber, t]);

  const isConfirmDisabled = !capacity || (mode === 'series' && !planCount);

  const handleConfirm = () => {
    if (!activeStartWeek || !capacity) return;

    onConfirm({
      startWeekNumber: activeStartWeek.weekNumber,
      capacity,
      planCount: mode === 'single' ? 1 : planCount,
      weeksBetweenPlans: mode === 'single' ? 0 : weeksBetweenPlans,
      lastWeekNumber
    });
    resetForm();
  };

  if (!isOpen) return null;

  const startWeekField = (
    <Dropdown
      label={t('add-treatment-plan-batch-start-week-label')}
      selectedOption={activeStartWeek ? weekDisplay(activeStartWeek) : undefined}
    >
      <DropdownOptions
        options={weeks}
        selectedOption={activeStartWeek}
        getKey={(week) => week.weekNumber}
        displayOption={(week) => weekDisplay(week)}
        handleChange={(value) => setStartWeek(value)}
      />
    </Dropdown>
  );

  const capacityField = (
    <InputFieldNumber
      label={t('add-treatment-plan-batch-capacity-label')}
      value={capacity}
      onChange={(e) => setCapacity(e.target.value ? Number(e.target.value) : 0)}
    />
  );

  return (
    <Modal
      title={modalTitle}
      onClose={handleClose}
      width={568}
      footer={
        <div className={styles.footer}>
          <Button type="secondary" onClick={handleClose}>
            {t('add-treatment-plan-batch-cancel')}
          </Button>
          <Button type="primary" disabled={isConfirmDisabled} onClick={handleConfirm}>
            {mode === 'single'
              ? t('add-treatment-plan-batch-confirm-single')
              : t('add-treatment-plan-batch-confirm')}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <div className={styles.templateRow}>
          <p className={styles.templateLabel}>{t('add-treatment-plan-batch-template-label')}</p>
          <p className={styles.templateName}>{templateName}</p>
        </div>

        {startWeekField}
        {capacityField}

        {mode === 'series' && (
          <>
            <InputFieldNumber
              label={t('add-treatment-plan-batch-plan-count-label')}
              value={planCount}
              onChange={(e) => setPlanCount(e.target.value ? Number(e.target.value) : 0)}
            />

            {planCount >= MIN_PLAN_COUNT_FOR_GAP && (
              <Dropdown
                label={t('add-treatment-plan-batch-gap-label')}
                selectedOption={t(`add-treatment-plan-batch-gap-${weeksBetweenPlans}`)}
              >
                <DropdownOptions
                  options={WEEKS_BETWEEN_PLANS_OPTIONS}
                  selectedOption={weeksBetweenPlans}
                  getKey={(option) => option}
                  displayOption={(option) => t(`add-treatment-plan-batch-gap-${option}`)}
                  handleChange={(value) => setWeeksBetweenPlans(value)}
                />
              </Dropdown>
            )}

            {summary && <p className={styles.summary}>{summary}</p>}
          </>
        )}
      </div>
    </Modal>
  );
}
